import { useEffect, useRef, useState } from 'react';
import { HeldReleasesFilters } from '@pages/api/artist-watchlist/types';
import { fetcher, KeyedMutator, useSWRFetch } from '@hooks/useSWRFetch';
import type { Request } from '@hooks/useSWRFetch/types';
import { TEN_MINUTES_IN_MS } from '@helpers/constants';
import type { Status } from '@generated/grpc/google/rpc';
import type {
  AlbumMetadata,
  AlbumMetadataListResponse,
  GetBlockedAlbumsCursorResponse,
  Approval,
  CheckRequestStatusByAlbumResponse,
} from '@generated/grpc/spotify/artistwatchlist/v1';

const SUCCESS_CODE = 0;
const ERROR_CODE = -1;
const MAX_ACCUMULATION_FETCHES = 10;

interface UseHeldReleasesResponse<T> {
  data: T | null | undefined;
  ok: boolean | undefined;
  isLoading: boolean;
  mutate: KeyedMutator<T>;
  hasMore: boolean;
}

// Build query params for cursor endpoint
function buildCursorParams(
  licensorIds: string[],
  limit: number,
  offset: number,
  sortedByInsertedDateAscending: boolean,
  filter?: HeldReleasesFilters,
): URLSearchParams {
  const params = new URLSearchParams();
  params.append('limit', limit.toString());
  params.append('offset', offset.toString());
  params.append(
    'sortedByInsertedDateAscending',
    sortedByInsertedDateAscending.toString(),
  );
  params.append('dateRange', filter?.onHeldDateFilter ?? '');
  licensorIds.forEach(id => params.append('licensorIds', id));
  return params;
}

export const useHeldReleases = (
  licensorIds: string[],
  currentPage: number,
  pageSize: number,
  sortedByInsertedDateAscending: boolean,
  filter?: HeldReleasesFilters,
): UseHeldReleasesResponse<AlbumMetadataListResponse> => {
  const [pageOffsets, setPageOffsets] = useState<number[]>([0]);
  const [accumulatedAlbums, setAccumulatedAlbums] = useState<AlbumMetadata[]>(
    [],
  );
  const [isAccumulating, setIsAccumulating] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const abortRef = useRef(false);
  // Refs for values needed inside effects but that shouldn't trigger re-runs
  const licensorIdsRef = useRef(licensorIds);
  licensorIdsRef.current = licensorIds;
  const filterRef = useRef(filter);
  filterRef.current = filter;

  const startOffset = pageOffsets[currentPage - 1] ?? 0;
  // Stable primitives for dependency arrays
  const licensorIdsKey = licensorIds.join(',');
  const dateRange = filter?.onHeldDateFilter ?? '';

  // Build the initial SWR request for the cursor endpoint
  const params = buildCursorParams(
    licensorIds,
    pageSize,
    startOffset,
    sortedByInsertedDateAscending,
    filter,
  );
  const requestObject: Request = {
    url: `catalog/api/artist-watchlist/cursor?${params}`,
    baseUrl: '',
  };

  const result = useSWRFetch<GetBlockedAlbumsCursorResponse>(
    licensorIds.length ? requestObject : null,
    {
      revalidateOnFocus: true,
      dedupingInterval: TEN_MINUTES_IN_MS,
    },
  );

  // Accumulation effect: when initial fetch completes, check if we need more
  useEffect(() => {
    abortRef.current = false;

    const batch = (result.data as GetBlockedAlbumsCursorResponse)?.batch;
    if (!batch) {
      return () => {
        abortRef.current = true;
      };
    }

    const initialAlbums = batch.albums ?? [];
    const batchHasMore = batch.hasMore ?? false;
    let nextOffset = batch.nextOffset ?? 0;

    // If we already have enough or no more data, we're done
    if (initialAlbums.length >= pageSize || !batchHasMore) {
      const albums = initialAlbums.slice(0, pageSize);
      setAccumulatedAlbums(albums);
      setHasMore(batchHasMore || initialAlbums.length >= pageSize);
      setIsAccumulating(false);
      // Store the offset for the next page
      setPageOffsets(prev => {
        const newOffsets = [...prev];
        newOffsets[currentPage] = nextOffset;
        return newOffsets;
      });
      return () => {
        abortRef.current = true;
      };
    }

    // Need to accumulate more results
    setIsAccumulating(true);
    let collected = [...initialAlbums];

    const fetchMore = async () => {
      let fetchCount = 0;

      while (
        collected.length < pageSize &&
        fetchCount < MAX_ACCUMULATION_FETCHES
      ) {
        if (abortRef.current) return;
        await new Promise(resolve => setTimeout(resolve, 500));
        fetchCount++;

        const moreParams = buildCursorParams(
          licensorIdsRef.current,
          pageSize - collected.length,
          nextOffset,
          sortedByInsertedDateAscending,
          filterRef.current,
        );

        try {
          const response = await fetcher<GetBlockedAlbumsCursorResponse>({
            url: `catalog/api/artist-watchlist/cursor?${moreParams}`,
            baseUrl: '',
          });

          const moreBatch = (response.data as GetBlockedAlbumsCursorResponse)
            ?.batch;
          if (!moreBatch) break;

          collected = [...collected, ...(moreBatch.albums ?? [])];
          nextOffset = moreBatch.nextOffset ?? nextOffset;

          if (!moreBatch.hasMore) {
            setHasMore(false);
            break;
          }
        } catch {
          break;
        }
      }

      if (!abortRef.current) {
        setAccumulatedAlbums(collected.slice(0, pageSize));
        setHasMore(collected.length >= pageSize);
        setIsAccumulating(false);
        setPageOffsets(prev => {
          const newOffsets = [...prev];
          newOffsets[currentPage] = nextOffset;
          return newOffsets;
        });
      }
    };

    fetchMore();

    return () => {
      abortRef.current = true;
    };
  }, [
    result.data,
    currentPage,
    pageSize,
    licensorIdsKey,
    sortedByInsertedDateAscending,
    dateRange,
  ]);

  // Reset page offsets when filter/sort/pageSize changes
  useEffect(() => {
    setPageOffsets([0]);
    setAccumulatedAlbums([]);
  }, [pageSize, sortedByInsertedDateAscending, dateRange]);

  // Build the response in the same shape as the old hook
  const assembledData: AlbumMetadataListResponse | null = result.data
    ? {
        albumMetadataListResponse: {
          albumMetadata: accumulatedAlbums,
        },
      }
    : null;

  return {
    data: assembledData,
    ok: result.ok,
    isLoading: result.isLoading || isAccumulating,
    mutate: result.mutate as unknown as KeyedMutator<AlbumMetadataListResponse>,
    hasMore,
  };
};

export const useHeldReleasesRequest = () => {
  const [mutationData, setMutationData] = useState<Status>();
  const [error, setError] = useState<Status>();
  const [isMutating, setIsMutating] = useState(false);

  const trigger = async (approval: Approval) => {
    setMutationData(undefined);
    setIsMutating(true);
    setError(undefined);

    const requestObject = {
      baseUrl: '',
      method: 'POST',
      body: JSON.stringify({ approval }),
    };

    try {
      const response = await fetch(
        '/catalog/api/artist-watchlist',
        requestObject,
      );
      const jsonData: Status = await response.json();

      if (jsonData.code === SUCCESS_CODE) {
        setMutationData(jsonData);
      } else {
        setError(jsonData);
      }
    } catch (e) {
      setError({
        code: ERROR_CODE,
        details: e,
      });
    }

    setIsMutating(false);
  };

  return { trigger, isMutating, mutationData, error };
};

interface useHeldReleasesRequestStatusResponse {
  response?: CheckRequestStatusByAlbumResponse;
  error?: Error;
}

export const useHeldReleasesRequestStatus = (
  albumUri: string,
): UseHeldReleasesResponse<useHeldReleasesRequestStatusResponse> => {
  const searchParams = new URLSearchParams();
  searchParams.append('albumUri', albumUri);

  const requestObject: Request = {
    url: `catalog/api/artist-watchlist/request-status?${new URLSearchParams(
      searchParams,
    )}`,
    baseUrl: '',
  };

  const result = useSWRFetch<useHeldReleasesRequestStatusResponse>(
    requestObject,
    {
      revalidateOnFocus: true,
      dedupingInterval: TEN_MINUTES_IN_MS,
    },
  );

  return { ...result, hasMore: false };
};
